/**
 * Remark plugin turning `[[cite:S1,S4]]` markers into renderable nodes.
 *
 * The pattern is the mirror of the server-side `MARKER_RE` in
 * `api/app/agent/citations/validator.py`. Keeping the two identical matters:
 * the server strips or rewrites any marker whose ids it did not hand out, so
 * anything reaching the client is already validated, and a client-side pattern
 * that matched *more* would render text the server never approved.
 *
 * Why this syntax is safe to split on: `[[` and `]]` are not CommonMark or GFM
 * syntax, so a marker survives Markdown parsing as literal text; the `cite:`
 * prefix means wiki-style `[[Page]]` links and ordinary French prose in double
 * brackets never match; and the id alphabet is three letters plus digits, so a
 * malformed marker fails the pattern rather than being misattributed.
 */
import type { Root, Text } from "mdast";
import type { Plugin } from "unified";
import { visit } from "unist-util-visit";

/** Mirror of MARKER_RE in api/app/agent/citations/validator.py. */
export const CITATION_MARKER = /\[\[cite:([SWC][0-9]{1,3}(?:\s*,\s*[SWC][0-9]{1,3})*)\]\]/g;

/** A citation node injected in place of a marker inside a text node. */
interface CitationNode {
  type: "citation";
  value: string;
  data: {
    hName: "citation";
    hProperties: { ids: string };
  };
}

function citationNode(ids: string[]): CitationNode {
  const joined = ids.join(",");
  return {
    type: "citation",
    value: joined,
    data: { hName: "citation", hProperties: { ids: joined } },
  };
}

/** Split `[[cite:S1,S2]]` into the ids it names. */
export function parseMarkerIds(group: string): string[] {
  return group
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Replace citation markers inside text nodes with `citation` nodes.
 *
 * Runs on text only, so a marker that happens to sit inside a code span or a
 * fenced block is left alone: code is quoted, not narrated, and rewriting it
 * would corrupt an example.
 */
export const remarkCitations: Plugin<[], Root> = () => (tree) => {
  visit(tree, "text", (node: Text, index, parent) => {
    if (!parent || index === undefined) return;
    const value = node.value;
    CITATION_MARKER.lastIndex = 0;
    if (!CITATION_MARKER.test(value)) return;

    CITATION_MARKER.lastIndex = 0;
    const children: Array<Text | CitationNode> = [];
    let cursor = 0;
    let match: RegExpExecArray | null;

    while ((match = CITATION_MARKER.exec(value)) !== null) {
      if (match.index > cursor) {
        children.push({ type: "text", value: value.slice(cursor, match.index) });
      }
      children.push(citationNode(parseMarkerIds(match[1])));
      cursor = match.index + match[0].length;
    }
    if (cursor < value.length) {
      children.push({ type: "text", value: value.slice(cursor) });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parent.children.splice(index, 1, ...(children as any[]));
    return index + children.length;
  });
};
